/**
 * Starting points, not filters.
 *
 * Each look is a plain recipe patch: applying one writes ordinary slider and
 * curve values that are then visible in every panel and revertible one control
 * at a time. There is no hidden preset layer, and nothing here is applied
 * automatically, which is the same contract the assistant's proposals honour.
 */

import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { defaultRecipe } from '@lumos/core';
import { PRESETS, applyPreset, type Preset } from '../../presets/presets.ts';
import { useSelectedPhoto, useStore } from '../../state/store.ts';
import { GroupLabel, Panel } from '../controls/Panel.tsx';
import { Tip } from '../controls/Tooltip.tsx';

/** The qualifier in a preset name, carried as weight rather than punctuation. */
const NOTE: CSSProperties = { marginLeft: 5, opacity: 0.62 };

/** The chip's full name, for the toast that follows applying it. */
function fullName(preset: Preset): string {
  return preset.note ? `${preset.name} ${preset.note}` : preset.name;
}

export function LooksPanel() {
  const photo = useSelectedPhoto();
  const setRecipe = useStore((state) => state.setRecipe);
  const notify = useStore((state) => state.notify);

  const recipe = photo?.recipe ?? null;

  // A look is "on" when the recipe still matches what it would produce, so
  // moving any slider afterwards honestly drops the highlight.
  const activeId = useMemo(() => {
    if (!recipe) return null;
    const current = JSON.stringify(recipe);
    return (
      PRESETS.find((preset) => JSON.stringify(applyPreset(recipe, preset)) === current)?.id ?? null
    );
  }, [recipe]);

  if (!recipe) return null;

  const apply = (preset: Preset) => {
    setRecipe(applyPreset(recipe, preset), { commit: true });
    notify(`Applied ${fullName(preset)}. What it moved is in the panels below.`);
  };

  const clear = () => {
    const neutral = defaultRecipe();
    // Framing and white balance are decisions about this frame, not about the
    // look, so clearing a look must not throw them away.
    neutral.temperature = recipe.temperature;
    neutral.tint = recipe.tint;
    neutral.crop = recipe.crop ? { ...recipe.crop } : null;
    neutral.rotate = recipe.rotate;
    neutral.orientation = recipe.orientation;
    neutral.flipHorizontal = recipe.flipHorizontal;
    neutral.flipVertical = recipe.flipVertical;
    neutral.masks = structuredClone(recipe.masks);
    setRecipe(neutral, { commit: true });
  };

  return (
    <Panel id="looks" title="Looks" active={activeId !== null}>
      <GroupLabel>
        <Tip
          title="Looks"
          body="Each one just sets ordinary sliders. Whatever it moves shows up in the panels below."
        >
          Starting points
        </Tip>
      </GroupLabel>
      <div className="suggestions">
        {PRESETS.map((preset) => {
          const on = preset.id === activeId;
          return (
            // The description is what each look costs as well as what it does.
            // It belongs one hover away, like every other explanation in the
            // rail, rather than printed under the chips.
            <Tip key={preset.id} title={fullName(preset)} body={preset.description}>
              <button
                type="button"
                className={`suggestion${on ? ' on' : ''}`}
                aria-pressed={on}
                onClick={() => apply(preset)}
              >
                {/* A mark rather than a colour, since the chip's applied state has
                    to survive a monochrome screen and a colour-blind reader. */}
                {on ? '✓ ' : ''}
                {preset.name}
                {preset.note ? <span style={NOTE}>{preset.note}</span> : null}
              </button>
            </Tip>
          );
        })}
        <Tip
          title="Clear"
          body="Puts the look controls back to neutral. White balance, crop and masks stay."
        >
          <button type="button" className="suggestion" onClick={clear}>
            Clear
          </button>
        </Tip>
      </div>
    </Panel>
  );
}
